import { useState } from 'react';
import { toast } from 'sonner';
import { Trans, useTranslation } from 'react-i18next';
import { CheckCircle2, Phone, Send, ShieldAlert } from 'lucide-react';
import { useAuth } from '@/providers/AuthProvider';
import { useRemovePhone, useUpdatePhone, useVerifyPhone } from '@/api/hooks/useAccount';
import type { AppError } from '@/api/client';

const E164 = /^\+[1-9]\d{1,14}$/;
const SIX_DIGIT = /^\d{6}$/;

type Step = 'display' | 'add' | 'verify';

/**
 * Phone management card on the customer account page. Drives the
 * Sprint 7 Evening 8 verification flow:
 *
 * <ul>
 *   <li>{@code display} — current state. Shows verified badge, or a
 *       "Not verified" prompt if a number is on file but unverified.</li>
 *   <li>{@code add} — phone number input (E.164). Submitting calls
 *       PUT /users/me/phone, which fires the Twilio Verify code.</li>
 *   <li>{@code verify} — 6-digit code input. Submitting calls
 *       POST /users/me/phone/verify; on success the AuthProvider
 *       re-fetches /users/me so the verified badge appears without
 *       a page reload.</li>
 * </ul>
 */
export default function PhoneSection() {
  const { t } = useTranslation();
  const { user, refreshUser } = useAuth();

  const [step, setStep] = useState<Step>('display');
  const [phoneInput, setPhoneInput] = useState('');
  const [codeInput, setCodeInput] = useState('');
  const [error, setError] = useState<string | null>(null);

  const updatePhone = useUpdatePhone();
  const verifyPhone = useVerifyPhone();
  const removePhone = useRemovePhone();

  const submitPhone = () => {
    setError(null);
    if (!E164.test(phoneInput)) {
      setError(t('account.phone_format_error'));
      return;
    }
    updatePhone.mutate(
      { phone: phoneInput },
      {
        onSuccess: async () => {
          await refreshUser();
          setStep('verify');
          setCodeInput('');
        },
        onError: (err) => {
          const e = err as unknown as AppError;
          setError(e?.message ?? t('account.phone_send_error'));
        },
      },
    );
  };

  const submitCode = () => {
    setError(null);
    if (!SIX_DIGIT.test(codeInput)) {
      setError(t('account.phone_code_length_error'));
      return;
    }
    verifyPhone.mutate(
      { code: codeInput },
      {
        onSuccess: async () => {
          await refreshUser();
          setStep('display');
          setPhoneInput('');
          setCodeInput('');
          toast.success(t('account.phone_verified_toast'));
        },
        onError: (err) => {
          const e = err as unknown as AppError;
          setError(e?.message ?? t('account.phone_invalid_code_error'));
        },
      },
    );
  };

  const resendCode = () => {
    if (!user?.phone) return;
    updatePhone.mutate(
      { phone: user.phone },
      {
        onSuccess: () => toast.success(t('account.phone_code_sent_toast')),
        onError: (err) => {
          const e = err as unknown as AppError;
          toast.error(e?.message ?? t('account.phone_resend_error'));
        },
      },
    );
  };

  const remove = () => {
    removePhone.mutate(undefined, {
      onSuccess: async () => {
        await refreshUser();
        setStep('display');
        toast.success(t('account.phone_removed_toast'));
      },
      onError: (err) => {
        const e = err as unknown as AppError;
        toast.error(e?.message ?? t('account.phone_remove_error'));
      },
    });
  };

  return (
    <Card>
      <header className="mb-3 flex items-center gap-2">
        <Phone className="h-4 w-4 text-neutral-500" aria-hidden="true" />
        <h2 className="text-sm font-semibold uppercase tracking-wider text-neutral-500">
          {t('account.phone_number')}
        </h2>
      </header>

      {step === 'display' && (
        <DisplayState
          user={user}
          onAdd={() => {
            setStep('add');
            setPhoneInput(user?.phone ?? '');
            setError(null);
          }}
          onEnterCode={() => {
            setStep('verify');
            setCodeInput('');
            setError(null);
          }}
          onResend={resendCode}
          onRemove={remove}
          busy={removePhone.isPending || updatePhone.isPending}
        />
      )}

      {step === 'add' && (
        <AddState
          phone={phoneInput}
          onChange={setPhoneInput}
          onSubmit={submitPhone}
          onCancel={() => {
            setStep('display');
            setError(null);
          }}
          busy={updatePhone.isPending}
          error={error}
        />
      )}

      {step === 'verify' && (
        <VerifyState
          phone={user?.phone ?? phoneInput}
          code={codeInput}
          onChange={setCodeInput}
          onSubmit={submitCode}
          onResend={resendCode}
          onCancel={() => {
            setStep('display');
            setError(null);
          }}
          busy={verifyPhone.isPending || updatePhone.isPending}
          error={error}
        />
      )}
    </Card>
  );
}

function DisplayState({
  user,
  onAdd,
  onEnterCode,
  onResend,
  onRemove,
  busy,
}: {
  user: { phone: string | null; phoneVerified: boolean } | null;
  onAdd: () => void;
  onEnterCode: () => void;
  onResend: () => void;
  onRemove: () => void;
  busy: boolean;
}) {
  const { t } = useTranslation();

  if (!user?.phone) {
    return (
      <div className="space-y-3">
        <p className="text-sm text-neutral-600">{t('account.phone_add_description')}</p>
        <button
          type="button"
          onClick={onAdd}
          className="inline-flex items-center gap-1.5 rounded-md bg-[#C2713A] px-4 py-2 text-sm font-semibold text-white hover:bg-[#a95f2f]"
        >
          {t('account.phone_add_button')}
        </button>
      </div>
    );
  }

  if (user.phoneVerified) {
    return (
      <div className="space-y-3">
        <div className="flex items-center gap-3">
          <span className="font-mono text-sm text-neutral-900">{user.phone}</span>
          <span className="inline-flex items-center gap-1 rounded-full border border-emerald-200 bg-emerald-50 px-2 py-0.5 text-xs font-medium text-emerald-700">
            <CheckCircle2 className="h-3 w-3" aria-hidden="true" />
            {t('account.phone_verified_badge')}
          </span>
        </div>
        <div className="flex gap-2">
          <button
            type="button"
            onClick={onAdd}
            disabled={busy}
            className="rounded-md border border-neutral-200 px-3 py-1.5 text-xs font-medium text-neutral-700 hover:bg-neutral-50 disabled:opacity-50"
          >
            {t('account.phone_change_button')}
          </button>
          <button
            type="button"
            onClick={onRemove}
            disabled={busy}
            className="rounded-md border border-neutral-200 px-3 py-1.5 text-xs font-medium text-red-600 hover:bg-red-50 disabled:opacity-50"
          >
            {busy ? t('account.phone_removing_button') : t('account.phone_remove_button')}
          </button>
        </div>
      </div>
    );
  }

  // Phone on file but not verified.
  return (
    <div className="space-y-3">
      <div className="flex items-center gap-3">
        <span className="font-mono text-sm text-neutral-900">{user.phone}</span>
        <span className="inline-flex items-center gap-1 rounded-full border border-amber-200 bg-amber-50 px-2 py-0.5 text-xs font-medium text-amber-700">
          <ShieldAlert className="h-3 w-3" aria-hidden="true" />
          {t('account.phone_not_verified_badge')}
        </span>
      </div>
      <div className="flex flex-wrap gap-2">
        <button
          type="button"
          onClick={onEnterCode}
          className="rounded-md bg-[#C2713A] px-3 py-1.5 text-xs font-semibold text-white hover:bg-[#a95f2f]"
        >
          {t('account.phone_verify_button')}
        </button>
        <button
          type="button"
          onClick={onResend}
          disabled={busy}
          className="rounded-md border border-neutral-200 px-3 py-1.5 text-xs font-medium text-neutral-700 hover:bg-neutral-50 disabled:opacity-50"
        >
          {t('account.phone_resend_button')}
        </button>
        <button
          type="button"
          onClick={onRemove}
          disabled={busy}
          className="rounded-md border border-neutral-200 px-3 py-1.5 text-xs font-medium text-red-600 hover:bg-red-50 disabled:opacity-50"
        >
          {t('account.phone_remove_button')}
        </button>
      </div>
    </div>
  );
}

function AddState({
  phone,
  onChange,
  onSubmit,
  onCancel,
  busy,
  error,
}: {
  phone: string;
  onChange: (v: string) => void;
  onSubmit: () => void;
  onCancel: () => void;
  busy: boolean;
  error: string | null;
}) {
  const { t } = useTranslation();
  return (
    <form
      onSubmit={(e) => {
        e.preventDefault();
        onSubmit();
      }}
      className="space-y-3"
    >
      <p className="text-xs text-neutral-500">
        <Trans
          i18nKey="account.phone_input_description"
          components={{ code: <code className="font-mono" /> }}
        />
      </p>
      <div className="flex gap-2">
        <input
          type="tel"
          value={phone}
          onChange={(e) => onChange(e.target.value.trim())}
          placeholder={t('account.phone_placeholder')}
          className="w-56 rounded-md border border-neutral-200 bg-white px-3 py-2 font-mono text-sm focus-visible:border-[#C2713A] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#C2713A]/30"
          autoComplete="tel"
        />
        <button
          type="submit"
          disabled={busy || !phone}
          className="inline-flex items-center gap-1.5 rounded-md bg-[#C2713A] px-4 py-2 text-sm font-semibold text-white hover:bg-[#a95f2f] disabled:cursor-not-allowed disabled:opacity-50"
        >
          <Send className="h-3.5 w-3.5" aria-hidden="true" />
          {busy ? t('account.phone_sending_button') : t('account.phone_send_code_button')}
        </button>
      </div>
      {error && <p className="text-xs text-red-600">{error}</p>}
      <button
        type="button"
        onClick={onCancel}
        className="text-xs text-neutral-500 hover:underline"
      >
        {t('common.cancel')}
      </button>
    </form>
  );
}

function VerifyState({
  phone,
  code,
  onChange,
  onSubmit,
  onResend,
  onCancel,
  busy,
  error,
}: {
  phone: string | null;
  code: string;
  onChange: (v: string) => void;
  onSubmit: () => void;
  onResend: () => void;
  onCancel: () => void;
  busy: boolean;
  error: string | null;
}) {
  const { t } = useTranslation();
  return (
    <form
      onSubmit={(e) => {
        e.preventDefault();
        onSubmit();
      }}
      className="space-y-3"
    >
      <p className="text-xs text-neutral-500">
        <Trans
          i18nKey="account.phone_code_description"
          values={{ phone: phone ?? '' }}
          components={{ phone: <span className="font-mono text-neutral-700" /> }}
        />
      </p>
      <div className="flex gap-2">
        <input
          type="text"
          inputMode="numeric"
          maxLength={6}
          value={code}
          onChange={(e) => onChange(e.target.value.replace(/\D/g, '').slice(0, 6))}
          placeholder={t('account.code_placeholder')}
          className="w-40 rounded-md border border-neutral-200 bg-white px-3 py-2 text-center font-mono text-lg tracking-widest text-neutral-900 focus-visible:border-[#C2713A] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#C2713A]/30"
          autoComplete="one-time-code"
        />
        <button
          type="submit"
          disabled={busy || code.length !== 6}
          className="rounded-md bg-[#C2713A] px-4 py-2 text-sm font-semibold text-white hover:bg-[#a95f2f] disabled:cursor-not-allowed disabled:opacity-50"
        >
          {busy ? t('account.phone_verifying_button') : t('account.phone_verify_submit_button')}
        </button>
      </div>
      {error && <p className="text-xs text-red-600">{error}</p>}
      <div className="flex gap-3 text-xs">
        <button
          type="button"
          onClick={onResend}
          disabled={busy}
          className="text-[#C2713A] hover:underline disabled:opacity-50"
        >
          {t('account.phone_resend_button')}
        </button>
        <button
          type="button"
          onClick={onCancel}
          className="text-neutral-500 hover:underline"
        >
          {t('common.cancel')}
        </button>
      </div>
    </form>
  );
}

function Card({ children }: { children: React.ReactNode }) {
  return (
    <section className="rounded-xl border border-neutral-200 bg-white p-5 shadow-sm">
      {children}
    </section>
  );
}
